import { useCallback, useEffect, useMemo } from "react";
import type { Product } from "@b2bportal/purchase-api";
import { useCheckoutSend, useCheckoutStateSelector } from "@checkout/context";
import {
  getIsRewardsSelected,
  type RewardsPaymentEvents,
  setRewardsAmountToPay,
  setRewardsAmountToPayInCash,
  setRewardsSelected,
  setRewardsUnselected,
} from "@hsbc/rewards";
import { useZeroAmount } from "@hsbc/utilities";
import { useBalanceWithTravelCredits } from "@hsbc/wallet";

interface UseRewardsSelectionReturn {
  context: {
    rewardsSelected: boolean;
    isRewardsDisabled: boolean;
  };
  handlers: {
    onApplyPointsClick: () => void;
    onDoNotApplyPointsClick: () => void;
  };
}

export const useRewardsSelection = (
  product?: Product
): UseRewardsSelectionReturn => {
  const send = useCheckoutSend<RewardsPaymentEvents>();
  const rewardsSelected = useCheckoutStateSelector(getIsRewardsSelected);
  const { fiat } = useZeroAmount();

  const { remaingCashBalance } = useBalanceWithTravelCredits(product);

  // Disable when travel credits cover the cost already
  const isRewardsDisabled = useMemo(
    () => remaingCashBalance <= 0,
    [remaingCashBalance]
  );

  const onApplyPointsClick = useCallback(
    () => send(setRewardsSelected()),
    [send]
  );

  const resetRewards = useCallback(() => {
    send(setRewardsUnselected());
    send(setRewardsAmountToPay(0));
    send(setRewardsAmountToPayInCash(fiat));
  }, [fiat, send]);

  useEffect(() => {
    if (isRewardsDisabled) {
      resetRewards();
    }
  }, [resetRewards, isRewardsDisabled]);

  return {
    context: {
      rewardsSelected,
      isRewardsDisabled,
    },
    handlers: {
      onApplyPointsClick,
      onDoNotApplyPointsClick: resetRewards,
    },
  };
};
